import React, { useEffect, useRef, useState } from 'react';
import {
  ALL_ALERTS,
  ALERT_CHANNEL_LABELS,
  type AlertFilterType,
} from '../../../types/alertFilter';
import styles from './alertTypeFilterDropdown.module.css';

interface AlertTypeFilterDropdownProps {
  value: AlertFilterType;
  onChange: (type: AlertFilterType) => void;
  /** Delivery channels present in the API response (email / api / sms). */
  availableChannels: AlertFilterType[];
}

const AlertTypeFilterDropdown: React.FC<AlertTypeFilterDropdownProps> = ({
  value,
  onChange,
  availableChannels,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const disabled = availableChannels.length <= 1;

  // Only show "All Alerts" when multiple channels have data.
  const options = disabled
    ? availableChannels.map(channel => ({
        value: channel,
        text: ALERT_CHANNEL_LABELS[channel],
      }))
    : [
        { value: ALL_ALERTS, text: ALERT_CHANNEL_LABELS[ALL_ALERTS] },
        ...availableChannels.map(channel => ({
          value: channel,
          text: ALERT_CHANNEL_LABELS[channel],
        })),
      ];

  const selected = options.find(o => o.value === value);
  const selectedLabel = selected ? selected.text : ALERT_CHANNEL_LABELS[ALL_ALERTS];

  // Close the menu when clicking outside the component.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (optionValue: AlertFilterType) => {
    onChange(optionValue);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => !disabled && setOpen(prev => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.triggerLabel}>{selectedLabel}</span>
        {!disabled && <span className={styles.chevron} />}
      </button>
      {open && (
        <ul className={styles.menu} role="listbox">
          {options.map(option => (
            <li
              key={String(option.value)}
              role="option"
              aria-selected={option.value === value}
              className={`${styles.option} ${
                option.value === value ? styles.optionSelected : ''
              }`}
              onClick={() => handleSelect(option.value as AlertFilterType)}
            >
              {option.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AlertTypeFilterDropdown;
